import * as Sentry from '@sentry/react';

import { getEnvironmentVariables } from '~/environment';

export interface Logger {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
}

class ConsoleLogger implements Logger {
  constructor(private readonly tag: string) {}

  debug(message: string, ...args: unknown[]) {
    console.debug(`[${this.tag}] ${message}`, ...args);
  }

  info(message: string, ...args: unknown[]) {
    console.info(`[${this.tag}] ${message}`, ...args);
  }

  warn(message: string, ...args: unknown[]) {
    console.warn(`[${this.tag}] ${message}`, ...args);
  }

  error(message: string, ...args: unknown[]) {
    console.error(`[${this.tag}] ${message}`, ...args);
  }
}

/**
 * Logger that uses Sentry.
 *
 * NOTE: This logger does not support passing arguments to the logger.
 *
 * @param tag - The tag to use for the logger.
 * @returns A logger that uses Sentry.
 */
class SentryLogger implements Logger {
  constructor(private readonly tag: string) {}

  debug(message: string, ...args: unknown[]) {
    Sentry.logger.debug(`[${this.tag}] ${message}`, ...(args as Record<string, unknown>[]));
  }

  info(message: string, ...args: unknown[]) {
    Sentry.logger.info(`[${this.tag}] ${message}`, ...(args as Record<string, unknown>[]));
  }

  warn(message: string, ...args: unknown[]) {
    Sentry.logger.warn(`[${this.tag}] ${message}`, ...(args as Record<string, unknown>[]));
  }

  error(message: string, ...args: unknown[]) {
    Sentry.logger.error(`[${this.tag}] ${message}`, ...(args as Record<string, unknown>[]));
  }
}

export function createLogger(tag: string) {
  const vars = getEnvironmentVariables();
  if (vars.isDeployed || vars.useSentry) {
    return new SentryLogger(tag);
  } else {
    return new ConsoleLogger(tag);
  }
}
